// app_client/src/components/modals/ModuleFormModal.jsx
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  X, Save, Terminal, Hash, 
  Layout, Box, Info, ShieldCheck 
} from 'lucide-react';
import useModuleStore from '../../store/useModuleStore';
import { toast } from 'react-hot-toast';

const ModuleFormModal = ({ isOpen, onClose }) => {
  const { createModule } = useModuleStore();
  
  // React Hook Form Kurulumu
  const { 
    register, 
    handleSubmit, 
    reset, 
    formState: { errors, isSubmitting } 
  } = useForm({
    defaultValues: {
      isActive: true,
      icon: 'Box' // Varsayılan ikon
    }
  });

  // Modal kapandığında veya açıldığında formu temizle
  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  const onSubmit = async (data) => {
    try {
      // Backend'e gönderim
      await createModule(data);
      toast.success(`${data.label} modülü başarıyla tanımlandı.`);
      onClose();
    } catch (err) {
      // Backend'den gelen spesifik hata mesajını göster (örn: "Bu isim zaten var")
      toast.error(err.response?.data?.message || "Modül eklenirken bir hata oluştu.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-xl rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 border border-white/20">
        
        {/* Header Bölümü */}
        <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-600 rounded-2xl text-white shadow-lg shadow-indigo-100">
              <ShieldCheck size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-800 tracking-tight">Yeni Modül Kaydı</h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-0.5">Sistem Yetenek Tanımlama</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-3 hover:bg-white rounded-2xl text-slate-400 hover:text-slate-600 transition-all border border-transparent hover:border-slate-100 shadow-sm"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
          
          <div className="grid grid-cols-2 gap-5">
            {/* 1. Sistem Anahtarı (Name) */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-1 tracking-widest">Sistem Anahtarı (Key)</label>
              <div className="relative group">
                <Terminal className="absolute left-4 top-4 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={18} />
                <input 
                  {...register("name", { 
                    required: "Sistem anahtarı zorunludur",
                    pattern: { value: /^[a-z0-9]+$/, message: "Sadece küçük harf ve rakam" }
                  })}
                  placeholder="örn: inventory"
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-300"
                />
              </div>
              {errors.name && <p className="text-[9px] font-bold text-rose-500 ml-1 uppercase">{errors.name.message}</p>}
            </div>

            {/* 2. Görünen İsim (Label) */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-1 tracking-widest">Menü Etiketi (Label)</label>
              <div className="relative group">
                <Hash className="absolute left-4 top-4 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={18} />
                <input 
                  {...register("label", { required: "Menü ismi zorunludur" })}
                  placeholder="örn: Stok Yönetimi"
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-300"
                />
              </div>
              {errors.label && <p className="text-[9px] font-bold text-rose-500 ml-1 uppercase">{errors.label.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-5">
            {/* 3. Rota (Path) */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-1 tracking-widest">URL Rotası (Path)</label>
              <div className="relative group">
                <Layout className="absolute left-4 top-4 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={18} />
                <input 
                  {...register("path", { required: "Path zorunludur" })}
                  placeholder="/inventory"
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-300 font-mono"
                />
              </div>
            </div>

            {/* 4. İkon Adı */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-1 tracking-widest">Lucide İkon Adı</label>
              <div className="relative group">
                <Box className="absolute left-4 top-4 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={18} />
                <input 
                  {...register("icon")}
                  placeholder="örn: Package, Users"
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-300"
                />
              </div>
            </div>
          </div>

          {/* 5. Açıklama */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-1 tracking-widest">Modül Açıklaması</label>
            <div className="relative group">
              <Info className="absolute left-4 top-4 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={18} />
              <textarea 
                {...register("description")}
                rows="2"
                placeholder="Bu modülün işlevi nedir?"
                className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-300 resize-none"
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="pt-4 flex gap-4">
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-slate-600 transition-colors"
            >
              Vazgeç
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="flex-2 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all shadow-xl shadow-indigo-100 active:scale-[0.98] disabled:opacity-50"
            >
              <Save size={18} /> 
              {isSubmitting ? 'Kaydediliyor...' : 'Modülü Tanımla'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModuleFormModal;